import {
  type ViewContext,
  minutesSince,
  timeOf,
  useActiveAlarm,
  useAlarmActions,
  useChildAlarmStatus,
} from '@shkola/api';
import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { INK, PillIcon, grad, pillClass } from '../design/forms';
import { ArrowUpRightIcon, CheckIcon, SirenIcon, WindIcon } from '../design/icons';

/*
 * The alarm across the top of every other page, in «Форми 2» (28.10) — the band of the student's home: an
 * air alarm on the orange gradient with white words, a drill on butter with the ink; the siren in a circle
 * that pulses; what to do now in a big light line; the buttons as pills, «Відкрити» the dark one. On
 * «Тривога» itself the page says all of it, so the band steps aside there.
 */

/** Re-renders every 15 s, for live timers. */
export function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/**
 * The ground of an alarm: an air alarm on a deep orange — a softer white glow in the corner than the cards
 * have, so the white words stay clear from the first letter; a drill on butter with the ink.
 */
export const alarmGround = (drill: boolean) =>
  drill
    ? grad('butter')
    : 'radial-gradient(90% 70% at 0% 0%, rgb(255 255 255 / 0.2) 0%, rgb(255 255 255 / 0) 60%), linear-gradient(165deg, #FF8A5B 0%, #FF6B3D 50%, #EE5427 100%)';

/** The siren in a pulsing circle: white with the orange siren on an air alarm, orange on a drill. */
export function SirenMark({ drill, size = 56 }: { drill: boolean; size?: number }) {
  return (
    <span
      className="relative flex shrink-0 items-center justify-center rounded-full"
      style={{ width: size, height: size, backgroundColor: drill ? '#FF6B3D' : '#FFFFFF' }}
    >
      <span
        className="absolute inset-0 animate-ping rounded-full opacity-40"
        style={{ backgroundColor: drill ? '#FF6B3D' : '#FFFFFF' }}
      />
      <SirenIcon
        size={Math.round(size * 0.44)}
        weight="fill"
        aria-hidden
        className="relative"
        color={drill ? '#FFFFFF' : '#FF6B3D'}
      />
    </span>
  );
}

/** How the child was marked in the roll-call (families and the student). */
export function ChildLine({
  ctx,
  sessionId,
}: {
  ctx: Extract<ViewContext, { kind: 'guardian' | 'student' }>;
  sessionId: string;
}) {
  const { t } = useTranslation();
  const status = useChildAlarmStatus(sessionId, ctx.child.memberId);
  if (!status.data) return <span>{t('alarm.child.pending', { name: ctx.child.firstName })}</span>;
  return (
    <span className="font-medium">
      {t('alarm.child.marked', {
        name: ctx.child.firstName,
        status: t(`alarm.status.${status.data.status}`),
        time: timeOf(status.data.marked_at),
      })}
    </span>
  );
}

/** Shown on top of every page while the school has an open alarm session. */
export function AlarmBanner({ ctx }: { ctx: ViewContext }) {
  const { t } = useTranslation();
  const alarm = useActiveAlarm(ctx.schoolId);
  const actions = useAlarmActions();
  const path = useRouterState({ select: (s) => s.location.pathname });
  useNow();
  const session = alarm.data;
  if (!session || path === '/alarm') return null;
  const kind = t(`alarm.kinds.${session.kind}`);
  const drill = session.kind === 'drill';

  const message =
    ctx.kind === 'guardian'
      ? t('alarm.banner.guardian', { kind: kind.toLowerCase() })
      : ctx.kind === 'student'
        ? t('alarm.banner.student', { kind })
        : t('alarm.banner.staff', { kind });

  return (
    <div
      role="alert"
      className={`flex min-w-0 flex-col gap-4 !rounded-[28px] p-4 md:p-5 ${ctx.kind === 'student' ? 'min-[1700px]:flex-row min-[1700px]:items-center' : 'lg:flex-row lg:items-center'}`}
      style={{ backgroundImage: alarmGround(drill), color: drill ? INK : '#FFFFFF' }}
    >
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <span className="md:hidden">
          <SirenMark drill={drill} size={48} />
        </span>
        <span className="hidden md:block">
          <SirenMark drill={drill} />
        </span>
        <div className="min-w-0">
          <p className="font-display text-[20px] leading-[26px] font-light tracking-[-0.02em] md:text-[24px] md:leading-[30px]">
            {message}
          </p>
          <p className="mt-1 text-[14px] leading-5 opacity-90">
            {t('alarm.duration', { minutes: minutesSince(session.started_at) })}
            {(ctx.kind === 'guardian' || ctx.kind === 'student') && (
              <>
                {' · '}
                <ChildLine ctx={ctx} sessionId={session.id} />
              </>
            )}
            {ctx.kind === 'guardian' && (
              <span className="mt-0.5 block font-medium">{t('alarm.banner.noPickup')}</span>
            )}
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {ctx.kind === 'student' && (
          <button
            type="button"
            className={pillClass('light')}
            onClick={() =>
              actions.mark.mutate({
                schoolId: ctx.schoolId,
                sessionId: session.id,
                marks: [{ studentId: ctx.child.memberId, status: 'safe_elsewhere' }],
              })
            }
          >
            {t('alarm.imSafe')}
            <PillIcon icon={CheckIcon} />
          </button>
        )}
        {ctx.kind === 'student' && (
          <Link to="/care" search={{ open: 'calm' }} className={pillClass('light')}>
            {t('care.calm.short')}
            <PillIcon icon={WindIcon} />
          </Link>
        )}
        {ctx.kind === 'student' && (
          <Link to="/alarm" className={pillClass('light')}>
            {t('shelter.open')}
            <PillIcon icon={ArrowUpRightIcon} />
          </Link>
        )}
        <Link to="/alarm" className={pillClass('dark')}>
          {t('alarm.banner.open')}
          <PillIcon icon={ArrowUpRightIcon} />
        </Link>
      </div>
    </div>
  );
}
